import { Invalid, Neutral, Valid, type Validator } from "#root/utility/validators";
import { type TextareaHTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";
import type { Transform } from "../../utility/transforms";
import { useFieldSet } from "./fieldset";

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  inboundTransform?: Transform<unknown, string | undefined>;
  outboundTransform?: Transform<string | undefined, unknown>;
  validator?: Validator;
  required?: boolean;
  dirty?: boolean;
};

export function TextArea({
                           inboundTransform = (value: unknown) => (value ? String(value) : undefined),
                           outboundTransform = (value: string | undefined) => value,
                           validator,
                           required,
                           dirty: startDirty,
                           className = "",
                           ...inputProps
                         }: TextareaProps) {
  const fieldSet = useFieldSet();

  const [value, setValue] = useState(inboundTransform(fieldSet?.field?.value));
  const [dirty, setDirty] = useState(startDirty);

  // console.log({ field: fieldSet.field, value,dirty,open,results});

  const { validity, hint } = useMemo(() => {
    if (value == null || value.length === 0) {
      if (required) {
        return { validity: Invalid, hint: "Required" };
      }

      return { validity: Neutral, hint: "Optional" };
    }

    if (validator) {
      return validator(value) || { validity: Valid, hint: "Valid" };
    }

    return { validity: Valid, hint: "Valid" };
  }, [value, required, validator]);

  useEffect(() => {
    fieldSet?.mutate(object => ({
      ...object,
      dirty,
      validity,
      hint,
      value: outboundTransform(value),
    }));
  }, [fieldSet, dirty, validity, hint, value, outboundTransform]);

  switch (validity) {
    case Valid: {
      className += "valid";

      break;
    }

    case Invalid: {
      className += "invalid";

      break;
    }

    case Neutral: {
      className += "neutral";

      break;
    }
    // No default
  }

  const handleChange = useCallback(evnt => {
    setValue(evnt.target.value);
    setDirty(true);
  }, []);

  const handleFocus = useCallback(() => {
    fieldSet?.mutate(object => ({ ...object, focused: true }));
  }, [fieldSet]);

  const handleBlur = useCallback(() => {
    fieldSet?.mutate(object => ({ ...object, focused: false }));
  }, [fieldSet]);

  const borderColor = validity === Invalid ? "border-red-400" : validity === Valid ? "border-green-400" : "border-[#ddd]";

  return (
    <textarea {...inputProps}
              className={`w-full border ${borderColor} rounded px-3 py-2 text-base text-[#333] outline-none focus:border-[#39f] focus:ring-2 focus:ring-[#39f]/20 transition-colors bg-white ${className}`}
              value={value} onChange={handleChange} onInput={handleChange} onFocus={handleFocus} onBlur={handleBlur}/>
  );
}
